
import styled from 'styled-components';
import Label from '../../../element/ui/core/label';

const StyledAnnotationSidebarActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const StyledOptions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  margin-top: 8px;
  flex-wrap: nowrap;
  gap: 0;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  .respark_annotation-options-editable {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  &.respark_annotations-options--locked .respark_annotation-options-editable {
    opacity: 0.55;
    pointer-events: none;
    user-select: none;
  }
`;

const ANNOTATION_OPTION_POPUP_MIN_WIDTH = 300;
const POSITION_POPUP_MIN_WIDTH = 168;

const StyledOptionPopupContent = styled.div`
  background: var(--theme-overlay-bg, #fff);
  box-shadow: 0 8px 28px var(--theme-overlay-shadow, rgba(15, 23, 42, 0.12));
  border: 1px solid var(--theme-overlay-border, #dee1ec);
  color: var(--theme-overlay-text, #1f2a44);
  border-radius: 8px;
  overflow: visible;
  min-width: ${ANNOTATION_OPTION_POPUP_MIN_WIDTH}px;
  box-sizing: border-box;

  &:has(.respark_annotation-option-panel--position) {
    min-width: ${POSITION_POPUP_MIN_WIDTH}px;
    width: max-content;
  }

  .respark_annotation-option-panel {
    padding-top: 12px;
  }

  * {
    font-family: 'Poppins', sans-serif;
  }
`;

const StyledSpacedOptionFields = styled.div`
  padding: 14px 16px 16px;
  min-width: ${ANNOTATION_OPTION_POPUP_MIN_WIDTH}px;
  box-sizing: border-box;
  overflow: visible;

  &.respark_annotation-option-panel--position {
    min-width: ${POSITION_POPUP_MIN_WIDTH}px;
    width: max-content;
    padding: 14px 12px 18px;
    overflow: visible;
  }
`;

const StyledHeadline = styled(Label)`
  display: block;
  font-weight: 600;
  font-size: 13px;
  line-height: 1.3;
  margin: 0 0 14px;
  color: var(--primary-text-color, #1f2a44);
`;

const StyledOptionFieldLabel = styled(Label)`
  display: block;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  margin: 0 0 12px;
  color: #5c6573;
`;

const StyledOptionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const StyledOptionField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
`;

const StyledOptionIconRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
`;

const StyledOptionSliderSlot = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  min-width: 118px;
  min-height: 72px;
  flex-shrink: 0;
  overflow: visible;
  padding-bottom: 4px;
`;

const StyledOptionFieldGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(118px, 1fr) minmax(118px, 1fr);
  column-gap: 20px;
  row-gap: 32px;
  margin-bottom: 12px;
  width: 100%;
`;

const StyledOptionFieldCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 118px;
  overflow: visible;

  > label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
    margin: 0 0 10px;
    flex-shrink: 0;
    color: #5c6573;
  }
`;

const StyledOptionColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 8px;
  margin-top: 4px;
  border-top: 1px solid #eef0f4;

  ${StyledOptionFieldLabel} {
    margin-bottom: 0;
    flex-shrink: 0;
  }
`;

/** @deprecated Use StyledOptionFieldGrid — kept for any legacy imports */
const StyledTwoColumnsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  row-gap: 16px;
  margin-bottom: 12px;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;

  > label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 8px;
  }
`;

const StyledIconWrapper = styled.div(
  ({ noMargin, secondaryIconColor }) => `
    cursor: pointer;
    padding: 0;
    margin: ${noMargin ? 0 : '0 0 10px'};
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 100px;
    width: 100px;
    min-height: 40px;
    height: 40px;
    box-sizing: border-box;
    border-radius: 6px;
    color: ${
      secondaryIconColor
        ? 'var(--theme-overlay-muted, #5c6573)'
        : 'var(--theme-overlay-text, #1f2a44)'
    };
    transition: background 0.15s ease, color 0.15s ease;

    svg {
      display: block;
      margin: 0;
      width: 20px;
      height: 20px;
      color: inherit;
    }

    &:hover {
      background: var(--theme-overlay-hover, rgba(255, 255, 255, 0.06));
      color: var(--theme-overlay-text, #1f2a44);
    }

    &[aria-selected='true'] {
      background: var(--theme-overlay-icon-bg, rgba(248, 85, 142, 0.18));
      color: var(--accent-primary-active, #f8558e);
    }
  `,
);

const StyledPositionGrid = styled.div.attrs({
  className: 'respark_position-grid',
})`
  display: grid;
  grid-template-columns: repeat(3, 40px);
  gap: 8px;
  width: max-content;
  margin: 0 auto;
  padding-bottom: 2px;
  overflow: visible;

  ${StyledIconWrapper} {
    min-width: 40px;
    min-height: 40px;
    width: 40px;
    height: 40px;
    padding: 8px;
    margin: 0;
  }
`;

export {
  ANNOTATION_OPTION_POPUP_MIN_WIDTH,
  POSITION_POPUP_MIN_WIDTH,
  StyledHeadline,
  StyledOptionFieldLabel,
  StyledOptionStack,
  StyledOptionField,
  StyledOptionIconRow,
  StyledOptionSliderSlot,
  StyledOptionFieldGrid,
  StyledOptionFieldCell,
  StyledPositionGrid,
  StyledOptionColorRow,
  StyledTwoColumnsContainer,
  StyledColumn,
  StyledIconWrapper,
  StyledSpacedOptionFields,
  StyledAnnotationSidebarActions,
  StyledOptions,
  StyledOptionPopupContent,
};
